import { Accordion } from '@iwoca/orion';
import styles from './FAQs.module.css';
import { Text } from '@ui';

export const FAQs = ({
    loanDuration,
    isTopUpApproved,
    isOverdue = false
}: {
    loanDuration: number;
    isTopUpApproved?: boolean;
    isOverdue?: boolean;
}) => {
    return (
        <>
            <Text className="mb-m font-medium">Frequently asked questions</Text>

            {!isOverdue && (
                <>
                    {!isTopUpApproved ? (
                        <Accordion
                            frame
                            icon="questionMarkOutline"
                            title="Can I request more funds?"
                            small
                            data-testid="can-i-request-more-funds"
                        >
                            <p className={styles.faqText}>
                                You can make a request once your{' '}
                                <strong>
                                    principal is below two thirds of your credit
                                    limit{' '}
                                </strong>
                                - e.g. £20k if your limit is £30k.
                            </p>
                            <br />
                            <p className={styles.faqText}>
                                You can request a new credit limit{' '}
                                <strong>up to £500k</strong>, and we’ll give you
                                a decision within{' '}
                                <strong>1 business day</strong> based on updated
                                info like bank statements. If you’re requesting
                                up to £15k we’ll give you an{' '}
                                <strong>instant decision</strong>.
                            </p>
                            <br />
                            <p className={styles.faqText}>
                                When you take more funds, you’ll have{' '}
                                <strong>another {loanDuration}</strong> months
                                to pay off the loan.
                            </p>
                            <br />
                            <a
                                href="https://support.iwoca.co.uk/en/articles/498744-can-i-top-up-my-loan"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.link}
                            >
                                Read more
                            </a>
                        </Accordion>
                    ) : (
                        <>
                            <Accordion
                                frame
                                icon="questionMarkOutline"
                                title="How many times can I withdraw funds?"
                                small
                            >
                                You can withdraw funds as many times as you'd
                                like, provided your approval window is still
                                active. You can see how long you have left at
                                the top of this page.
                            </Accordion>

                            <Accordion
                                frame
                                icon="questionMarkOutline"
                                title="How much can I withdraw at once?"
                                small
                            >
                                You can withdraw any amount above £100, until
                                you reach your credit limit.
                            </Accordion>
                        </>
                    )}

                    <Accordion
                        frame
                        icon="questionMarkOutline"
                        title="How do early repayments work?"
                        small
                        data-testid="how-do-early-repayments-work"
                    >
                        <p className={styles.faqText}>
                            If you repay more than your agreed monthly
                            repayment, you’re technically repaying early –{' '}
                            <strong>there’s no fee for that</strong>.
                        </p>
                        <br />
                        <p className={styles.faqText}>
                            You can do it as a one-off via instant bank
                            transfer, your linked debit card, or manual
                            transfer.
                        </p>
                        <br />
                        <p className={styles.faqText}>
                            When you do, you can either pay off your full loan
                            there and then, or give yourself a{' '}
                            <strong>repayment break</strong> - e.g. if your
                            monthly payment is £1,000 a month, and you repay
                            £3,000 one month, you’ll have two months off.
                            (You’ll still accrue interest for this time,
                            though.)
                        </p>
                        <br />
                        <a
                            href="https://support.iwoca.co.uk/en/articles/592981-how-do-repayments-work"
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.link}
                        >
                            Read more
                        </a>
                    </Accordion>
                </>
            )}

            <Accordion
                frame
                icon="questionMarkOutline"
                title="What if I can't repay?"
                small
                data-testid="what-if-i-cant-repay"
            >
                <p className={styles.faqText}>
                    If you have trouble repaying, let us know so we can work
                    with you to find a solution. We might be able to lower your
                    monthly repayments, or move the date you pay them.
                </p>
                <br />
                <p className={styles.faqText}>
                    There are lots of different ways we can help, so please call
                    us on{' '}
                    <a href="tel:020 3397 3375">
                        <strong>020 3397 3375</strong>
                    </a>
                    .
                </p>
                <br />
                <a
                    href="https://support.iwoca.co.uk/en/articles/1608757-how-we-can-help-if-you-miss-a-payment-or-you-re-struggling-to-repay"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                >
                    Read more
                </a>
            </Accordion>
        </>
    );
};
